import { apiClient, type DownloadResult } from './client';

export interface DashboardStats {
  activeIntegrations: number;
  syncedRecords: number;
  activeWorkflows: number;
  eventsToday: number;
  recentSyncRuns: number;
  failedSyncsToday: number;
}

export interface IntegrationHealth {
  id: string;
  name: string;
  provider: string;
  status: string;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  syncSuccessRate: number;
}

export interface SyncHistoryPoint {
  date: string;
  syncs: number;
  successful: number;
  failed: number;
}

export interface RecentEvent {
  id: string;
  source: string;
  category: string;
  severity: string;
  title: string;
  createdAt: string;
}

export interface RecordCounts {
  companies: number;
  contacts: number;
  deals: number;
  invoices: number;
  subscriptions: number;
  meetings: number;
}

export interface MrrWaterfallPoint {
  date: string;
  mrr: number;
  newMrr: number;
  expansionMrr: number;
  contractionMrr: number;
  churnedMrr: number;
  netChange: number;
}

export type MrrMovementType = 'new' | 'expansion' | 'contraction' | 'churn';

export interface MrrMovementItem {
  companyName: string;
  type: MrrMovementType;
  previousMrr: number;
  currentMrr: number;
  delta: number;
}

export interface MrrCurrentMonth {
  newMrr: number;
  expansionMrr: number;
  contractionMrr: number;
  churnedMrr: number;
  netChange: number;
  movements: MrrMovementItem[];
}

export interface MrrOverview {
  currentMrr: number;
  currentArr: number;
  activeSubscriptions: number;
  currency: string;
  history: MrrWaterfallPoint[];
  currentMonth: MrrCurrentMonth;
}

export async function getDashboardStats(): Promise<{ stats: DashboardStats }> {
  return apiClient.get('/dashboard/stats');
}

export async function getRecentEvents(limit?: number): Promise<{ events: RecentEvent[] }> {
  return apiClient.get('/dashboard/events/recent', {
    params: limit ? { limit } : undefined,
  });
}

export async function getSyncHistory(days?: number): Promise<{ history: SyncHistoryPoint[] }> {
  return apiClient.get('/dashboard/sync-history', {
    params: days ? { days } : undefined,
  });
}

export async function getIntegrationHealth(): Promise<{ integrations: IntegrationHealth[] }> {
  return apiClient.get('/dashboard/integration-health');
}

export async function getRecordCounts(): Promise<{ counts: RecordCounts }> {
  return apiClient.get('/dashboard/record-counts');
}

export async function getMrrOverview(): Promise<{ overview: MrrOverview }> {
  return apiClient.get('/dashboard/mrr');
}

export interface MrrMonthDetailItem {
  companyName: string;
  planName: string;
  mrr: number;
  status: string;
  odooSubscriptionId: number;
}

export interface MrrMonthDetail {
  month: string;
  totalMrr: number;
  currency: string;
  odooBaseUrl: string;
  items: MrrMonthDetailItem[];
  movements: MrrMovementItem[];
}

export async function getMrrMonthDetail(month: string): Promise<{ detail: MrrMonthDetail }> {
  return apiClient.get(`/dashboard/mrr/${month}`);
}

export type CsmStatus = 'active' | 'inactive' | 'unmapped';
export type CsmStatusFilter = 'all' | CsmStatus;

export interface CsmSummary {
  activeCompanies: number;
  inactiveCompanies: number;
  activeUsers: number;
  inactiveUsers: number;
  unmappedUsers: number;
  totalCompanies: number;
  totalMembers: number;
}

export interface CsmCompany {
  id: string;
  externalId: string;
  name: string;
  domain: string | null;
  memberCount: number;
  mappedUserCount: number;
  unmappedUserCount: number;
  activeUsers30d: number;
  inactiveUsers30d: number;
  activeRate30d: number;
  loginCount30d: number;
  lastLoginAt: string | null;
  internalMemberCount: number;
  internalActiveUsers30d: number;
  internalLoginCount30d: number;
  lastInternalLoginAt: string | null;
  status: CsmStatus;
  healthScore: number | null;
  healthTier: string | null;
  healthUsageOnly: boolean | null;
  healthSnapshotDate: string | null;
  /** Points the usage-decline penalty took off the score; only on the company detail. */
  healthDeclinePenalty?: number | null;
  healthDeclineSeverity?: 'early_warning' | 'confirmed' | null;
}

export interface CsmOverviewResponse {
  summary: CsmSummary;
  companies: CsmCompany[];
  total: number;
  page: number;
  pageSize: number;
  lastProdSyncAt: string | null;
  lastAuth0SyncAt: string | null;
  lastCsmRefreshAt: string | null;
  auth0CoverageStartAt: string | null;
}

export interface CsmUser {
  id: string;
  externalId: string;
  name: string;
  email: string | null;
  userType: 'internal' | 'external';
  auth0UserId: string | null;
  role: string | null;
  loginCount30d: number;
  loginCount90d: number;
  lastLoginAt: string | null;
  hasEverLoggedIn: boolean;
  status: CsmStatus;
}

export interface CsmTokenHistoryPoint {
  date: string;
  totalTokens: number;
}

export interface CsmTokenSummary {
  totalTokens30d: number;
  lastTokenAt: string | null;
}

export interface CsmCompanyUsersResponse {
  company: CsmCompany;
  users: CsmUser[];
  tokenHistory: CsmTokenHistoryPoint[];
  tokenSummary: CsmTokenSummary;
}

export interface CsmWindow {
  from?: string;
  to?: string;
}

export async function getCsmOverview(params: {
  from?: string;
  to?: string;
  status?: CsmStatusFilter;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<CsmOverviewResponse> {
  return apiClient.get('/dashboard/csm', {
    params,
  });
}

export async function getCsmCompanyUsers(companyId: string, window: CsmWindow = {}): Promise<CsmCompanyUsersResponse> {
  return apiClient.get(`/dashboard/csm/${companyId}/users`, {
    params: { ...window },
  });
}

export async function exportCsmOverview(params: {
  from?: string;
  to?: string;
  status?: CsmStatusFilter;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<DownloadResult> {
  return apiClient.download('/dashboard/csm/export', {
    params,
  });
}

// ── CSM roster (Attio CSM list mirror — source of truth) ────────────────────

export interface CsmRosterRow {
  entryId: string;
  attioRecordId: string;
  companyName: string | null;
  domain: string | null;
  companyType: string | null;
  lifecycleStage: string | null;
  primaryCsmName: string | null;
  primaryCsmEmail: string | null;
  health: string | null;
  arr: number | null;
  /** The Attio list's odoo_mrr writeback. Not shown: it double-counts renewals. */
  odooMrr: number | null;
  /**
   * This month's MRR from the Finance MRR engine (same as Finance and Customers).
   * Null when the book has no MRR for the company or could not be built. Older APIs omit it.
   */
  mrr?: number | null;
  onboardingStage: string | null;
  matchedProdCompanyIds: string[];
  matchVia: string | null;
  matchReason: string | null;
  totalMembers: number;
  activeMembers30d: number;
  events30d: number;
  /** Classic + aOS (SEQUESTO staff left out of aOS), 30 days ending today. */
  tokens30d: number;
  /** The aOS share of tokens30d. */
  aosTokens30d?: number;
  lastActiveAt: string | null;
  syncedAt: string;
  /** Computed CS health score (daily CSM snapshot); a roll-up is the token-weighted score of its entities with usage (older APIs: the lowest). Null when not scored. Older APIs omit it. */
  computedHealth?: {
    score: number;
    tier: string;
    snapshotDate: string;
    /** Entities the score is built from. Older APIs omit it. */
    scoredEntities?: number;
    /** Entities with no tokens in 30 days, left out of a roll-up. Older APIs omit it. */
    idleEntitiesExcluded?: number;
    /** What the score is made of. Older APIs omit it; fields are null on rows stored before Oct 2026. */
    drivers?: CsmHealthDrivers | null;
  } | null;
}

/** Mirrors HealthDrivers in packages/api/src/modules/analytics/csm-roster.ts. */
export interface CsmHealthDrivers {
  usage: number;
  sentiment: number | null;
  recency: number | null;
  active: number | null;
  trend: number | null;
  activeRate30d: number | null;
  lastTokenDaysAgo: number | null;
  declinePenalty: number;
  declineSeverity: string | null;
  idleCap: boolean | null;
}

/** total = Σ rows' mrr + Σ notOnRoster. */
export interface CsmRosterMrr {
  month: string | null;
  /** The Finance MRR headline; null when the book could not be built. */
  total: number | null;
  onRoster: number;
  notOnRoster: Array<{ name: string; mrr: number }>;
  error: string | null;
}

export interface CsmRosterResponse {
  rows: CsmRosterRow[];
  /** Older APIs omit it. */
  mrr?: CsmRosterMrr;
  lastSyncedAt: string | null;
}

export interface CsmReconciliationResponse {
  unmatchedCustomers: Array<{
    entryId: string;
    attioRecordId: string;
    companyName: string | null;
    domain: string | null;
    osmatching: string | null;
    matchReason: string | null;
  }>;
  prodCompaniesNotOnList: Array<{
    prodCompanyId: string;
    name: string;
    domain: string | null;
    totalMembers: number;
    activeMembers30d: number;
    events30d: number;
    /** Classic tokens only. */
    tokens30d: number;
    /** aOS tokens by customer users; absent on an API that predates it. */
    aosTokens30d?: number;
    /** A workspace named after one of its own members (same rule as the usage bridges); absent on an older API. */
    personal?: boolean;
    lastActiveAt: string | null;
  }>;
  /**
   * Companies with a live Odoo subscription that the CSM list doesn't carry as a
   * live customer: missing from it, or on it as churned. Older APIs omit it.
   */
  payingNotOnList?: Array<{
    odooCompanyId: string;
    odooName: string;
    attioName: string | null;
    attioRecordId: string | null;
    /** EUR; null when a contract's currency couldn't be converted. */
    mrrEur: number | null;
    rosterStatus: 'missing' | 'churned';
    rosterCompanyName: string | null;
  }>;
  lastSyncedAt: string | null;
}

export async function getCsmRoster(all = false): Promise<CsmRosterResponse> {
  return apiClient.get('/dashboard/csm/roster', { params: { all } });
}

export async function getCsmReconciliation(): Promise<CsmReconciliationResponse> {
  return apiClient.get('/dashboard/csm/reconciliation');
}

// ── CSM usage insights (leaderboards + portfolio trends) ────────────────────

export interface CsmInsightsWindow {
  from: string;
  to: string;
}

export interface CsmLeaderboardUserRow {
  userId: string;
  name: string;
  email: string | null;
  companyNames: string[];
  events: number;
  activeDays: number;
  previousEvents: number;
  deltaPct: number | null;
}

export interface CsmLeaderboardCompanyRow {
  companyId: string;
  name: string;
  events: number;
  tokens: number;
  activeMembers: number;
  totalMembers: number;
  previousEvents: number;
  previousTokens: number;
  eventsDeltaPct: number | null;
  tokensDeltaPct: number | null;
  /**
   * Classic + aOS for the same company and windows, the figure At risk compares.
   * Only on topCompaniesByTokens; null when it couldn't be loaded. Older APIs omit it.
   */
  combined?: {
    aosTokens: number;
    previousAosTokens: number;
    tokens: number;
    previousTokens: number;
    deltaPct: number | null;
  } | null;
}

/** Whether the mirrored history reaches over the previous window; the API withholds deltas when it doesn't. */
export interface CsmInsightsCoverage {
  activityFrom: string | null;
  tokensFrom: string | null;
  previousActivityCovered: boolean;
  previousTokensCovered: boolean;
}

/** Platform total = on the CSM roster (live customers) + the companies listed here. */
export interface CsmRosterBridge {
  onRoster: number;
  /** `personal`: a workspace named after one of its own members (a person's space, not a company). Older APIs omit it. */
  notOnRoster: Array<{ name: string; tokens: number; listedAs: string | null; personal?: boolean }>;
}

export interface CsmLeaderboardsResponse {
  window: CsmInsightsWindow;
  previousWindow: CsmInsightsWindow;
  /** Older APIs omit it. */
  coverage?: CsmInsightsCoverage;
  topUsers: CsmLeaderboardUserRow[];
  topCompaniesByEvents: CsmLeaderboardCompanyRow[];
  /** Ranked by members with a login. Older APIs omit it; the page re-ranks topCompaniesByEvents instead. */
  topCompaniesByActiveUsers?: CsmLeaderboardCompanyRow[];
  topCompaniesByTokens: CsmLeaderboardCompanyRow[];
  /** Set when the Classic + aOS figures could not be loaded. Older APIs omit it. */
  combinedError?: string | null;
}

export interface CsmPortfolioTrendPoint {
  date: string;
  events: number;
  tokens: number;
  activeUsers: number;
  activeCompanies: number;
}

export interface CsmPortfolioTrendsResponse {
  window: CsmInsightsWindow;
  previousWindow: CsmInsightsWindow;
  /** Older APIs omit these. */
  coverage?: CsmInsightsCoverage;
  bridge?: CsmRosterBridge & { platformTokens: number };
  series: CsmPortfolioTrendPoint[];
  summary: {
    events: number;
    previousEvents: number;
    eventsDeltaPct: number | null;
    tokens: number;
    previousTokens: number;
    tokensDeltaPct: number | null;
    activeUsers: number;
    previousActiveUsers: number;
    activeUsersDeltaPct: number | null;
    activeCompanies: number;
    previousActiveCompanies: number;
  };
}

export async function getCsmLeaderboards(params: { from?: string; to?: string; days?: number } = {}): Promise<CsmLeaderboardsResponse> {
  return apiClient.get('/dashboard/csm/leaderboards', { params });
}

export async function getCsmPortfolioTrends(params: { from?: string; to?: string; days?: number } = {}): Promise<CsmPortfolioTrendsResponse> {
  return apiClient.get('/dashboard/csm/portfolio-trends', { params });
}

// ── CSM declining usage ─────────────────────────────────────────────────────

export interface CsmDecliningCompany {
  entryId: string;
  attioRecordId: string;
  companyName: string | null;
  primaryCsmName: string | null;
  primaryCsmEmail: string | null;
  severity: 'early_warning' | 'confirmed';
  weeklyEventsChangePct: number | null;
  weeklyTokensChangePct: number | null;
  monthlyEventsChangePct: number | null;
  monthlyTokensChangePct: number | null;
  events7d: number;
  events30d: number;
  eventsPrior30d: number;
  tokens7d: number;
  tokens30d: number;
  tokensPrior30d: number;
  /** The aOS share of tokens30d. */
  aosTokens30d?: number;
  /** The aOS share of tokensPrior30d. Older APIs omit it. */
  aosTokensPrior30d?: number;
  /** Login-event spike days in the look-back counted as an ordinary (median) day. Tokens are never clipped. Older APIs omit it. */
  clippedDays?: { events: number };
}

export interface CsmDecliningResponse {
  companies: CsmDecliningCompany[];
  /** Logins down while tokens rose: listed, not a decline (no health penalty, no alert). Older APIs omit it. */
  watch?: CsmDecliningCompany[];
  scopeCompanies: number;
  detectedAt: string;
}

export async function getCsmDeclining(): Promise<CsmDecliningResponse> {
  return apiClient.get('/dashboard/csm/declining');
}

// ── All-hands marketing export ───────────────────────────────────────────────

/**
 * Quick path: download the brief/CSV synchronously using the fast daily index
 * sample (no full sitemap crawl). Returns in seconds.
 */
export async function downloadMarketingExportQuick(format: 'md' | 'csv'): Promise<DownloadResult> {
  return apiClient.download('/gtm/marketing-export', {
    params: { format, indexCheck: 'snapshot' },
  });
}

/** Start the full export (crawls the whole sitemap for the accurate not-indexed count). */
export async function startMarketingExportRun(): Promise<{ runId: string }> {
  return apiClient.post('/gtm/marketing-export/runs');
}

export type MarketingExportRun =
  | { status: 'running' }
  | { status: 'ready'; markdown: string; csv: string; fileName: string; generatedAt: string }
  | { status: 'error'; error: string };

/** Poll a started full export run for completion. */
export async function getMarketingExportRun(runId: string): Promise<MarketingExportRun> {
  return apiClient.get(`/gtm/marketing-export/runs/${runId}`);
}

export interface CsmSnapshotByDay {
  snapshotDate: string;
  companies: number;
  persons: number;
  firstWrittenAt: string | null;
  lastWrittenAt: string | null;
}

export interface CsmSnapshotCompanyRow {
  snapshotDate: string;
  companyId: string;
  domain: string;
  memberCount: number;
  activeUsers30d: number;
  activeRate30d: number;
  loginCount30d: number;
  totalTokens30d: number;
  yesterdayTokens: number;
  status: string;
  lastLoginAt: string | null;
  lastTokenAt: string | null;
}

export interface CsmSnapshotHistoryResponse {
  health: {
    firstSnapshotDate: string | null;
    lastSnapshotDate: string | null;
    daysCovered: number;
    totalCompanyRows: number;
    totalPersonRows: number;
  };
  range: { from: string; to: string };
  byDay: CsmSnapshotByDay[];
  companyRows: CsmSnapshotCompanyRow[];
}

export async function getCsmSnapshotHistory(params: {
  from?: string;
  to?: string;
  companyId?: string;
  date?: string;
} = {}): Promise<CsmSnapshotHistoryResponse> {
  return apiClient.get('/dashboard/csm/snapshot-history', {
    params,
  });
}

// ── Token Usage aOS (new-platform ai_usage ledger) ──────────────────────────

export interface CsmAosSummary {
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  reasoningTokens: number;
  totalCalls: number;
  credits: number;
  activeCompanies: number;
  activeUsers: number;
  /** aOS tokens SEQUESTO staff used in customer workspaces, left out above. Older APIs omit it. */
  excludedStaffTokens?: number;
  /** First day the aOS ledger mirror holds. Older APIs omit it. */
  coverageStart?: string | null;
}

export interface CsmAosCompanyRow {
  id: string;
  externalId: string;
  /** The workspace name on the platform. */
  name: string;
  domain: string | null;
  /** The Attio company on the CSM list this workspace rolls up to. Older APIs omit it. */
  attioName?: string | null;
  attioRecordId?: string | null;
  /** A live customer on the CSM roster. Older APIs omit it. */
  onRoster?: boolean;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  reasoningTokens: number;
  calls: number;
  credits: number;
  activeUsers: number;
  lastEventAt: string | null;
}

export interface CsmAosSeriesPoint {
  date: string;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  calls: number;
  /** Distinct users with an AI call that day; calls with no user are not counted. */
  activeUsers: number;
  /** Distinct customer workspaces with an AI call that day. */
  activeCompanies: number;
}

export interface CsmAosBreakdownRow {
  key: string;
  totalTokens: number;
  calls: number;
}

export interface CsmAosOverviewResponse {
  summary: CsmAosSummary;
  /** Older APIs omit it. */
  bridge?: CsmRosterBridge & { onRosterCompanies: number };
  companies: CsmAosCompanyRow[];
  total: number;
  page: number;
  pageSize: number;
  series: CsmAosSeriesPoint[];
  byModel: CsmAosBreakdownRow[];
  byProvider: CsmAosBreakdownRow[];
  byOperationType: CsmAosBreakdownRow[];
  lastSyncAt: string | null;
  range: { from: string; to: string };
}

export interface CsmAosUserRow {
  userExternalId: string | null;
  name: string;
  email: string | null;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  calls: number;
}

export interface CsmAosProjectRow {
  projectExternalId: string | null;
  projectName: string | null;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  calls: number;
}

export interface CsmAosCompanyDetailResponse {
  company: CsmAosCompanyRow;
  /** Tokens SEQUESTO staff used in this workspace, left out of company and byUser. Older APIs omit it. */
  staffTokens?: number;
  series: CsmAosSeriesPoint[];
  byUser: CsmAosUserRow[];
  byProject: CsmAosProjectRow[];
  byModel: CsmAosBreakdownRow[];
  byOperationType: CsmAosBreakdownRow[];
  range: { from: string; to: string };
}

export async function getCsmAosOverview(params: {
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<CsmAosOverviewResponse> {
  return apiClient.get('/dashboard/csm/aos', { params });
}

export async function getCsmAosCompanyDetail(
  companyId: string,
  window: CsmWindow = {},
): Promise<CsmAosCompanyDetailResponse> {
  return apiClient.get(`/dashboard/csm/aos/${companyId}`, { params: { ...window } });
}
